import React, { useEffect, useState } from 'react';
import './AdminOrders.css';

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('orders'); // 'orders', 'history', 'cancelled', 'contacts'
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    setLoading(true);
    if (view === 'contacts') {
      fetch(`${API_BASE_URL}/contacts`)
        .then(res => res.json())
        .then(data => {
          setContacts(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching contacts:', err);
          setLoading(false);
        });
    } else {
      fetch(`${API_BASE_URL}/${view === 'history' ? 'order-history' : view === 'cancelled' ? 'cancelled-orders' : 'orders'}`)
        .then(res => res.json())
        .then(data => {
          setOrders(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching orders:', err);
          setLoading(false);
        });
    }
  }, [view]);

  const handleStatusUpdate = async (order, status) => {
    if (!order) return;

    if (status === 'Delivered') {
      const res = await fetch(`${API_BASE_URL}/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, status }),
      });

      const data = await res.json();
      if (data.success) {
        alert('Order marked as Delivered');
        setOrders(prev => prev.filter(o => o.id !== order.id));
      } else {
        alert('Failed to update status');
      }
    } else if (status === 'Cancelled') {
      const remark = prompt('Enter a remark for cancellation:');
      if (!remark) return;

      const res = await fetch(`${API_BASE_URL}/cancel-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order, remark }),
      });

      const data = await res.json();
      if (data.success) {
        alert('Order Cancelled');
        setOrders(prev => prev.filter(o => o.id !== order.id));
      } else {
        alert('Failed to cancel order');
      }
    }
  };

  const renderItems = (order) => {
    if (Array.isArray(order.items)) {
      return order.items.map((item, i) => (
        <div key={i}>
          {item.name} × {item.quantity ?? item.qty ?? 1}
        </div>
      ));
    }
    if (typeof order.items === 'string') {
      try {
        const parsedItems = JSON.parse(order.items);
        return parsedItems.map((item, i) => (
          <div key={i}>
            {item.name} × {item.quantity ?? item.qty ?? 1}
          </div>
        ));
      } catch {
        return <div>{order.items}</div>;
      }
    }
    if (order.customer?.cart) {
      return order.customer.cart.map((item, i) => (
        <div key={i}>
          {item.name} × {item.quantity ?? item.qty ?? 1}
        </div>
      ));
    }
    return <div>No items found</div>;
  };

  const formatAddress = (order) => {
    const customer = order.customer || {};
    return [
      customer.flat, customer.area, customer.landmark, customer.pincode,
      customer.city, customer.taluka, customer.dist, customer.state
    ].filter(Boolean).join(', ') || 'No address provided';
  };

  return (
    <div className="admin-orders-container">
      <h1>Welcome to Admin Panel</h1>

      <div style={{ marginBottom: '1rem' }}>
        <button onClick={() => setView('orders')}>Orders</button>
        <button onClick={() => setView('history')}>Order History</button>
        <button onClick={() => setView('cancelled')}>Cancelled Orders</button>
        <button onClick={() => setView('contacts')}>
          {view === 'contacts' ? 'Hide Contacts' : 'Contact Details'}
        </button>
      </div>

      <h2>
        {view === 'contacts'
          ? 'Contact Submissions'
          : view === 'history'
            ? 'Order History'
            : view === 'cancelled'
              ? 'Cancelled Orders'
              : 'Recent Orders'}
      </h2>

      {loading && <div className="loading">Loading...</div>}

      {view === 'contacts' ? (
        <table className="contacts-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Message</th>
              <th>Rating</th>
              <th>Submitted At</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 ? (
              <tr><td colSpan="6">No contact submissions found.</td></tr>
            ) : (
              contacts.map((contact, idx) => (
                <tr key={idx}>
                  <td>{contact.first_name} {contact.last_name}</td>
                  <td>{contact.email}</td>
                  <td>{contact.phone}</td>
                  <td>{contact.message}</td>
                  <td>{contact.rating}</td>
                  <td>{new Date(contact.submitted_at).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      ) : (
        <table className="orders-table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Amount (₹)</th>
              <th>Customer</th>
              <th>Address</th>
              <th>Items</th>
              <th>Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, idx) => (
              <tr key={idx}>
                <td data-label="Order ID">{order.id}</td>
                <td data-label="Amount (₹)">{Number(order.amount).toFixed(2)}</td>
                <td data-label="Customer">
                  {order.name || order.customer?.name}<br />
                  {order.email || order.customer?.email}<br />
                  {order.mobile || order.customer?.mobile}
                </td>
                <td data-label="Address">{order.address || formatAddress(order)}</td>
                <td data-label="Items">{renderItems(order)}</td>
                <td data-label="Date">{new Date(order.date).toLocaleString()}</td>
                <td data-label="Status">
                  {view === 'orders' ? (
                    <>
                      <button onClick={() => handleStatusUpdate(order, 'Delivered')}>Delivered</button>
                      <button onClick={() => handleStatusUpdate(order, 'Cancelled')}>Cancel</button>
                    </>
                  ) : (
                    <strong>
                      {order.status}
                      {order.remark ? ` (Remark: ${order.remark})` : ''}
                    </strong>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminOrders;
